import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cmm_admin_token";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const downloadCSV = (rows, filename, columns) => {
  if (!rows || !rows.length) return;
  const head = columns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = c.get(r);
      const s = v == null ? "" : (typeof v === "object" ? JSON.stringify(v) : String(v));
      return `"${s.replace(/"/g, '""')}"`;
    }).join(",")
  );
  const csv = [head, ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const Login = ({ onAuth }) => {
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/admin/login`, { token });
      localStorage.setItem(TOKEN_KEY, token);
      onAuth(token);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not log in.");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#050b14] text-white flex items-center justify-center px-5">
      <form onSubmit={submit} className="bg-white text-[#050b14] rounded-2xl w-full max-w-sm p-7 shadow-2xl" data-testid="admin-login">
        <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#008dd2] font-bold">Commodity Market Movers · Admin</div>
        <div className="font-display text-2xl font-extrabold mt-2" style={{ letterSpacing: "-0.025em" }}>Sign in</div>
        <input
          type="password"
          required value={token} onChange={(e) => setToken(e.target.value)}
          className="field-light mt-5 w-full" placeholder="Admin token"
          data-testid="admin-token-input"
          autoFocus
        />
        {err && <div className="text-red-600 text-sm mt-2" data-testid="admin-login-error">{err}</div>}
        <button type="submit" disabled={busy} className="btn btn-cyan w-full mt-4 disabled:opacity-50" data-testid="admin-login-btn">
          {busy ? "Signing in…" : "Sign in →"}
        </button>
      </form>
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="bg-white text-[#050b14] rounded-xl p-5 shadow-sm border border-[#e5eaf3]">
    <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[#050b14]/55 font-bold">{label}</div>
    <div className="font-display text-3xl font-extrabold mt-1 tabular-nums" style={{ letterSpacing: "-0.025em" }}>{value}</div>
    {sub && <div className="text-[#050b14]/55 text-xs mt-1">{sub}</div>}
  </div>
);

const Table = ({ columns, rows, testid }) => (
  <div className="bg-white rounded-xl overflow-hidden border border-[#e5eaf3] shadow-sm" data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-[#f7f9fc] text-[#050b14]/65 text-[0.7rem] uppercase tracking-[0.14em]">
          <tr>{columns.map((c) => <th key={c.label} className="text-left px-4 py-3 font-bold">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td className="px-4 py-8 text-center text-[#050b14]/50" colSpan={columns.length}>No records yet.</td></tr>}
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-[#eef1f6] hover:bg-[#f7f9fc]/60">
              {columns.map((c) => <td key={c.label} className="px-4 py-3 align-top text-[#050b14]">{c.cell ? c.cell(r) : c.get(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const TabBtn = ({ id, active, label, count, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${active ? "bg-[#050b14] text-white" : "bg-white text-[#050b14]/70 border border-[#e5eaf3] hover:border-[#050b14]/30"}`}
    data-testid={`admin-tab-${id}`}
  >
    {label} <span className={`ml-1 ${active ? "text-white/70" : "text-[#050b14]/40"}`}>{count}</span>
  </button>
);

const NomStatusBadge = (r) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[0.7rem] font-bold uppercase tracking-wider ${r.status === "paid" ? "bg-[#e7f8ed] text-[#0a8a3a]" : "bg-[#fff4d6] text-[#a67c00]"}`}>{r.status}</span>
);

const PayStatusBadge = (r) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[0.7rem] font-bold uppercase ${r.status === "paid" ? "bg-[#e7f8ed] text-[#0a8a3a]" : r.status === "failed" ? "bg-[#fde7e7] text-[#b00020]" : "bg-[#fff4d6] text-[#a67c00]"}`}>{r.status}</span>
);

const Dashboard = ({ token, onSignOut }) => {
  const [tab, setTab] = useState("registrations");
  const [overview, setOverview] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmState, setConfirmState] = useState("idle"); // idle | confirm | sending | done | error
  const [confirmTestEmail, setConfirmTestEmail] = useState("aadil@teflas.com");
  const [confirmTestState, setConfirmTestState] = useState("idle"); // idle | sending | done | error
  const [probeState, setProbeState] = useState("idle"); // idle | sending | done | error
  const [probeEmail, setProbeEmail] = useState("");
  const [probeResult, setProbeResult] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [ov, r, n, l, p] = await Promise.all([
        axios.get(`${API}/admin/overview`, { headers }),
        axios.get(`${API}/admin/registrations`, { headers }),
        axios.get(`${API}/admin/nominations`, { headers }),
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/payments`, { headers }),
      ]);
      setOverview(ov.data);
      setRegistrations(r.data || []);
      setNominations(n.data || []);
      setLeads(l.data || []);
      setPayments(p.data || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        onSignOut();
        return;
      }
      setErr(e?.response?.data?.detail || e?.message || "Failed to load.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const sendConfirmedTest = async () => {
    if (!confirmTestEmail.trim()) { setConfirmTestState("error"); setNotice("✗ Enter an email address for the test first."); return; }
    setConfirmTestState("sending"); setNotice("");
    try {
      const res = await axios.post(`${API}/admin/notify/confirmed`, { test_to: confirmTestEmail.trim() }, { headers });
      setConfirmTestState("done");
      setNotice(`✓ Test "It's confirmed" email sent to ${res.data.to} for approval. Resend id: ${res.data.resend_id}`);
    } catch (e) {
      setConfirmTestState("error");
      setNotice("✗ " + (e?.response?.data?.detail || e?.message || "Failed to send test email."));
    }
  };

  const sendConfirmedAll = async () => {
    setConfirmState("sending"); setNotice("");
    try {
      const res = await axios.post(`${API}/admin/notify/confirmed`, {}, { headers });
      setConfirmState("done");
      setNotice(`✓ Queued the "It's confirmed" email to ${res.data.total_recipients} unique ticket holders. Delivery takes ~${Math.ceil((res.data.eta_seconds || 60) / 60)} min.`);
    } catch (e) {
      setConfirmState("error");
      setNotice("✗ " + (e?.response?.data?.detail || e?.message || "Failed to queue broadcast."));
    }
  };

  const loadEmailCfg = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-config`, { headers });
      setEmailCfg(res.data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadEmailCfg(); }, []); // eslint-disable-line

  const fireProbe = async () => {
    if (!probeEmail.trim()) { setProbeState("error"); setProbeResult({ error: "Enter an email address first." }); return; }
    setProbeState("sending"); setProbeResult(null);
    try {
      const res = await axios.post(`${API}/admin/email/probe`, { to: probeEmail.trim() }, { headers });
      setProbeState("done");
      setProbeResult(res.data);
    } catch (e) {
      setProbeState("error");
      setProbeResult({ error: e?.response?.data?.detail || e?.message || "Probe failed." });
    }
  };

  const filter = (rows, fields) => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => fields.some((f) => String(f(r) || "").toLowerCase().includes(q)));
  };

  const regCols = [
    { label: "Date", get: (r) => fmtDate(r.created_at) },
    { label: "Name", get: (r) => r.name },
    { label: "Email", get: (r) => r.email },
    { label: "Phone", get: (r) => r.phone },
    { label: "Company", get: (r) => r.company || "—" },
    { label: "Designation", get: (r) => r.designation || "—" },
    { label: "Source", get: (r) => r.source || "—" },
    { label: "UTM", get: (r) => r.utm ? Object.entries(r.utm).map(([k,v]) => `${k}=${v}`).join(" · ") : "—" },
    { label: "Ref", get: (r) => `#${(r.id||"").slice(0,8).toUpperCase()}` },
  ];
  const nomCols = [
    { label: "Date", get: (r) => fmtDate(r.created_at) },
    { label: "Nominee", get: (r) => r.nominee?.name },
    { label: "Company", get: (r) => r.nominee?.company },
    { label: "Email", get: (r) => r.nominee?.email },
    { label: "Phone", get: (r) => r.nominee?.phone },
    { label: "Categories", get: (r) => (r.categories || []).join(" · ") },
    { label: "Cats", get: (r) => (r.categories || []).length },
    { label: "Total", get: (r) => fmtINR(r.pricing?.total) },
    { label: "Status", cell: NomStatusBadge, get: (r) => r.status },
    { label: "Ref", get: (r) => `#${(r.id||"").slice(0,8).toUpperCase()}` },
  ];
  const leadCols = [
    { label: "Date", get: (r) => fmtDate(r.created_at) },
    { label: "Source", get: (r) => r.source },
    { label: "Name", get: (r) => r.name },
    { label: "Email", get: (r) => r.email },
    { label: "Phone", get: (r) => r.phone || "—" },
    { label: "Company", get: (r) => r.company || "—" },
    { label: "Designation", get: (r) => r.designation || "—" },
    { label: "Score", get: (r) => r.score != null ? `${r.score}/10 · ${r.tier}` : "—" },
  ];
  const payCols = [
    { label: "Date", get: (r) => fmtDate(r.created_at) },
    { label: "Kind", get: (r) => r.kind },
    { label: "Order ID", get: (r) => r.order_id },
    { label: "Amount", get: (r) => fmtINR(r.amount_inr) },
    { label: "Status", cell: PayStatusBadge, get: (r) => r.status },
    { label: "Contact", get: (r) => r.contact?.email || r.contact?.name || "—" },
    { label: "Payment ID", get: (r) => r.payment_id || "—" },
  ];

  const tabBtn = (id, label, count) => (
    <TabBtn id={id} label={label} count={count} active={tab === id} onClick={() => setTab(id)} />
  );

  return (
    <div className="min-h-screen bg-[#f5f7fb] text-[#050b14]">
      <header className="bg-[#050b14] text-white px-5 lg:px-8 py-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#00d1ff] font-bold">Admin · CMM 2026</div>
          <div className="font-display text-xl font-extrabold leading-tight" style={{ letterSpacing: "-0.025em" }}>Commodity Market Movers</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} className="text-sm bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg" data-testid="admin-refresh">
            {loading ? "Refreshing…" : "Refresh ↻"}
          </button>
          <a href="/" className="text-sm text-white/65 hover:text-white px-3 py-2">View site</a>
          <button onClick={onSignOut} className="text-sm text-white/65 hover:text-white px-3 py-2" data-testid="admin-signout">Sign out</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {err && <div className="text-red-600 text-sm mb-4" data-testid="admin-error">{err}</div>}
        {notice && <div className={`text-sm mb-4 p-3 rounded-lg ${notice.startsWith("✓") ? "bg-[#e7f8ed] text-[#0a5c2b]" : "bg-[#fde7e7] text-[#8a0f0f]"}`} data-testid="admin-notice">{notice}</div>}

        {/* Email delivery probe */}
        <div className="mb-6 rounded-2xl border-2 border-[#008dd2]/40 bg-[#e8f7ff] p-4 lg:p-5" data-testid="admin-email-probe">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[0.65rem] uppercase tracking-[0.18em] font-bold text-[#005f8a]">Email delivery probe</div>
              <div className="font-display font-extrabold text-lg mt-1 leading-tight" style={{ letterSpacing: "-0.02em" }}>Test the confirmation-email pipeline end-to-end.</div>
              <div className="text-[#050b14]/60 text-xs mt-1">
                Sends the same TO + CC pipeline used by every real registration/nomination.
                {emailCfg && (
                  <span> Sender: <b className="font-mono">{emailCfg.sender_email}</b> · CC: <b className="font-mono">{(emailCfg.internal_cc || []).join(", ") || "(none)"}</b></span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="email"
                value={probeEmail}
                onChange={(e) => setProbeEmail(e.target.value)}
                placeholder="you@example.com"
                className="text-sm px-3 py-2 rounded-lg border border-[#050b14]/20 min-w-[240px]"
                data-testid="admin-probe-email"
              />
              <button
                onClick={fireProbe}
                disabled={probeState === "sending"}
                className="btn btn-cyan !py-2.5 !px-5 text-sm font-extrabold uppercase tracking-wider disabled:opacity-50"
                data-testid="admin-probe-send"
              >
                {probeState === "sending" ? "Sending…" : "Send probe →"}
              </button>
            </div>
          </div>
          {probeResult && (
            <div className={`mt-3 text-sm p-3 rounded-lg ${probeResult.error ? "bg-[#fde7e7] text-[#8a0f0f]" : "bg-[#e7f8ed] text-[#0a5c2b]"}`} data-testid="admin-probe-result">
              {probeResult.error ? (
                <>✗ {probeResult.error}</>
              ) : (
                <>
                  ✓ Queued to <b>{probeResult.to}</b>. CC: {(probeResult.cc || []).join(", ") || "(none)"}. Resend id: <span className="font-mono text-xs">{probeResult.resend_id}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Broadcast bar · "It's confirmed" */}
        <div className="mb-6 rounded-2xl border-2 border-[#f5c518]/50 bg-[#fff8dc] p-4 lg:p-5" data-testid="admin-broadcast">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[0.65rem] uppercase tracking-[0.18em] font-bold text-[#8a6b00]">Broadcast · "It's confirmed"</div>
              <div className="font-display font-extrabold text-lg mt-1 leading-tight" style={{ letterSpacing: "-0.02em" }}>Email every ticket holder the confirmed date &amp; venue.</div>
              <div className="text-[#050b14]/60 text-xs mt-1">Recipients: <b>{registrations.length}</b> · Subject: "Confirmed: Commodity Market Movers 2026 — 1 October, The Westin Mumbai Powai Lake"</div>
            </div>
          </div>

          {/* Step 1 — test / approval send */}
          <div className="mt-4 pt-4 border-t border-[#f5c518]/40">
            <div className="text-[0.62rem] uppercase tracking-[0.16em] font-bold text-[#8a6b00] mb-2">Step 1 · Send a test for approval</div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="email"
                value={confirmTestEmail}
                onChange={(e) => setConfirmTestEmail(e.target.value)}
                placeholder="aadil@teflas.com"
                className="flex-1 min-w-[220px] px-3 py-2.5 rounded-lg border border-[#050b14]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#f5c518] bg-white"
                data-testid="admin-confirmed-test-email"
              />
              <button
                onClick={sendConfirmedTest}
                disabled={confirmTestState === "sending"}
                className="btn btn-gold !py-2.5 !px-5 text-sm font-extrabold uppercase tracking-wider disabled:opacity-60"
                data-testid="admin-confirmed-test-send"
              >
                {confirmTestState === "sending" ? "Sending…" : "Send test →"}
              </button>
            </div>
          </div>

          {/* Step 2 — full broadcast */}
          <div className="mt-4 pt-4 border-t border-[#f5c518]/40 flex items-center gap-3 flex-wrap">
            <div className="text-[0.62rem] uppercase tracking-[0.16em] font-bold text-[#8a6b00] flex-1 min-w-[180px]">Step 2 · Send to all ticket holders (only after approval)</div>
            {confirmState === "idle" || confirmState === "error" ? (
              <button onClick={() => setConfirmState("confirm")} className="btn !bg-[#050b14] !text-white !py-2.5 !px-5 text-sm font-extrabold uppercase tracking-wider" data-testid="admin-confirmed-open">
                Send to all {registrations.length} →
              </button>
            ) : confirmState === "confirm" ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[#050b14]/70">Confirm — this cannot be undone.</span>
                <button onClick={() => setConfirmState("idle")} className="text-sm px-3 py-2 rounded-lg border border-[#050b14]/20 hover:bg-white" data-testid="admin-confirmed-cancel">Cancel</button>
                <button onClick={sendConfirmedAll} className="btn btn-gold !py-2.5 !px-5 text-sm font-extrabold uppercase tracking-wider" data-testid="admin-confirmed-confirm">Yes, send to all {registrations.length}</button>
              </div>
            ) : confirmState === "sending" ? (
              <span className="text-sm font-semibold text-[#8a6b00]" data-testid="admin-confirmed-sending">Queuing…</span>
            ) : (
              <span className="text-sm font-semibold text-[#0a5c2b]" data-testid="admin-confirmed-done">Queued ✓</span>
            )}
          </div>
        </div>

        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="admin-stats">
            <Stat label="Registrations" value={overview.registrations} />
            <Stat label="Nominations" value={overview.nominations} sub={`${overview.paid_nominations} paid`} />
            <Stat label="Brochure leads" value={overview.leads} />
            <Stat label="Orders created" value={overview.orders} sub={`${overview.paid_orders} captured`} />
            <Stat label="Revenue (paid)" value={fmtINR(overview.revenue_inr)} />
            <Stat label="Day" value={new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} sub="IST" />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap mt-7">
          {tabBtn("registrations", "Registrations", registrations.length)}
          {tabBtn("nominations", "Nominations", nominations.length)}
          {tabBtn("leads", "Brochure leads", leads.length)}
          {tabBtn("payments", "Payments", payments.length)}
          <div className="ml-auto flex items-center gap-2">
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…" className="field-light !py-2 text-sm w-48"
              data-testid="admin-search"
            />
            <button
              onClick={() => {
                if (tab === "registrations") downloadCSV(filter(registrations, [(r) => r.name, (r) => r.email, (r) => r.company, (r) => r.source]), "cmm-registrations.csv", regCols);
                if (tab === "nominations")   downloadCSV(filter(nominations,   [(r) => r.nominee?.name, (r) => r.nominee?.email, (r) => r.nominee?.company, (r) => (r.categories||[]).join(" ")]), "cmm-nominations.csv", nomCols);
                if (tab === "leads")         downloadCSV(filter(leads,         [(r) => r.name, (r) => r.email, (r) => r.company, (r) => r.source]), "cmm-leads.csv", leadCols);
                if (tab === "payments")      downloadCSV(filter(payments,      [(r) => r.order_id, (r) => r.contact?.email, (r) => r.kind, (r) => r.status]), "cmm-payments.csv", payCols);
              }}
              className="text-sm bg-[#050b14] text-white px-3 py-2 rounded-lg whitespace-nowrap"
              data-testid="admin-export"
            >
              Export CSV ↓
            </button>
          </div>
        </div>

        <div className="mt-5">
          {tab === "registrations" && <Table columns={regCols} rows={filter(registrations, [(r) => r.name, (r) => r.email, (r) => r.company, (r) => r.source])} testid="admin-table-registrations" />}
          {tab === "nominations"   && <Table columns={nomCols} rows={filter(nominations,   [(r) => r.nominee?.name, (r) => r.nominee?.email, (r) => r.nominee?.company, (r) => (r.categories||[]).join(" ")])} testid="admin-table-nominations" />}
          {tab === "leads"         && <Table columns={leadCols} rows={filter(leads,         [(r) => r.name, (r) => r.email, (r) => r.company, (r) => r.source])} testid="admin-table-leads" />}
          {tab === "payments"      && <Table columns={payCols} rows={filter(payments,      [(r) => r.order_id, (r) => r.contact?.email, (r) => r.kind, (r) => r.status])} testid="admin-table-payments" />}
        </div>
      </main>
    </div>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  if (!token) return <Login onAuth={setToken} />;
  return <Dashboard token={token} onSignOut={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />;
};

export default AdminPage;
